import HotelStatusIcon from 'components/HotelStatusIcon'
import { SecondarySmallButton } from 'components/ReusableComponents/Button/SmallButtons'
import { CheckIcon } from 'components/ReusableComponents/Icons/Check'
import { CrossIcon } from 'components/ReusableComponents/Icons/Cross'
import TryLoginDialog from 'components/TryLoginDialog'
import { PMSsWithSyncLoginSupport, userEmailsRestartPMS } from 'constants/index'
import { PMSnames } from 'main/hotelBrandsAndChains'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'

import NoteCell from '../../Common/NoteCell'
import useNote from '../../Common/useNote'
import { useNoteBatchLoader } from '../../Common/NoteBatchLoaderContext'
import { Link } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'
import ReactTooltip from 'react-tooltip'
import internalOnlyApi from 'services/api/internalOnly'
import pmsApi from 'services/api/pms'

import { Cell, Row, Spacer } from '../../../../components/ReusableComponents/Table'
import Toggle from '../../../../components/ReusableComponents/Toggle/index'
import HotelIdCell from '../../Common/HotelIdCell'
import HotelNameCell from '../../Common/HotelNameCell'
import { ReactComponent as BugIcon } from './bug.svg'
import ElectronTelemetryModal from '../ElectronTelemetryModal'
import { derivePepCsmAction } from '../derivePepCsmAction'
import { getPepHealthCheckDisplay } from '../parsePepHealthCheckLastResult'
import { getPepDesktopAppVersionDisplay } from '../pepDesktopAppVersion'
import {
  getEffectivePmsReportsInLast48Hours,
  isPmsSyncLastInfoStale
} from '../pmsSyncDataRecency'
import styles from './PMSBErow.module.scss'
import layoutStyles from '../PMSandBE.module.scss'

const PMS_HILTON_PEP = 'pep'

const NumberCell = ({ children, ...props }) => (
  <Cell className={styles.numberCell} {...props}>
    {children}
  </Cell>
)

const SyncRow = ({
  subsidiaryId,
  displayId,
  brand,
  shortName,
  groupName,
  pms,
  pmsHealthyKind,
  isAllReportHealthy,
  pmsSyncTotalReportsReceivedInLast48Hours,
  index,
  handleSetDebugData,
  problems,
  isHavingProblems,
  pmsSyncIsConnected,
  subsidiaryStatus,
  nextStartDate,
  lastResult,
  pmsSyncLastInfoReceivedOn,
  isDisabled,
  isInProgress,
  csmUserFullName,
  rowStatus,
  statusExplanation,
  hideStopTestLogin = false,
  compactPepLayout = false,
  latestDesktopRelease = null,
  showDesktopAppVersion = false,
  pepDesktopAppDeviceCount = 0,
  pepDesktopAppDistinctUserCount = null,
  pepDashboardUserCount = null,
  pepDesktopAppVersions = '',
  pepCredentialIssueKind = '',
  pepCredentialLastFailureReason = '',
  pepCredentialWorkingCount = 0,
  pepCredentialWrongPasswordCount = 0,
  pepCredentialChangePasswordCount = 0,
  pepPasswordExpireDays = null,
  openTelemetryOnMount = false,
  isTelemetryHighlighted = false,
  onTelemetryHighlight
}) => {
  const login = useSelector(state => state.login)
  const { registerCallback, notesCache } = useNoteBatchLoader()

  const {
    note,
    setNote,
    saveImmediately,
    isLoading: isLoadingNote,
    isSaving,
    error: noteError,
    loadNoteOnDemand,
    setNoteFromBatch
  } = useNote(subsidiaryId, 'healthCheck-pms', { batchLoad: true })

  // Register callback to receive batch-loaded notes
  useEffect(() => {
    const key = `${subsidiaryId}-healthCheck-pms`
    
    // Check if note is already in cache
    if (notesCache.has(key)) {
      setNoteFromBatch(notesCache.get(key))
    }

    // Register callback for future updates
    const unregister = registerCallback(subsidiaryId, 'healthCheck-pms', (data) => {
      setNoteFromBatch(data)
    })

    return unregister
  }, [subsidiaryId, registerCallback, notesCache, setNoteFromBatch])

  const canReEnablePMS =
    login.isInternalAdmin &&
    userEmailsRestartPMS.includes(login.currentUser.email) &&
    PMSsWithSyncLoginSupport.includes(pms)

  const isPMSSupportesLogin = PMSsWithSyncLoginSupport.includes(pms)

  const [reEnablingPmsSync, setReEnablingPmsSync] = useState(false)

  const [isTryLoginDialogVisible, setIsTryLoginDialogVisible] = useState(false)
  const [isTryingLogin, setIsTryingLogin] = useState(false)
  const [tryLoginResponse, setTryLoginResponse] = useState(null)
  const [isTryLoginSuccessful, setIsTryLoginSuccessful] = useState()
  const [isPMSSyncEnabled, setIsPMSSyncEnabled] = useState(!isDisabled)

  const [isElectronTelemetryOpen, setIsElectronTelemetryOpen] = useState(false)
  const telemetryAutoOpenedRef = useRef(false)

  const showElectronTelemetryButton =
    login.isInternalAdmin && pms === PMS_HILTON_PEP

  const openElectronTelemetry = () => {
    onTelemetryHighlight?.()
    setIsElectronTelemetryOpen(true)
  }

  useEffect(() => {
    if (!openTelemetryOnMount || !showElectronTelemetryButton || telemetryAutoOpenedRef.current) {
      return
    }

    openElectronTelemetry()
    telemetryAutoOpenedRef.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/deep-link open only
  }, [openTelemetryOnMount, showElectronTelemetryButton])

  const effectiveReportsInLast48Hours = getEffectivePmsReportsInLast48Hours(
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours
  )

  const pepHealthDisplay = getPepHealthCheckDisplay(
    lastResult,
    pms,
    effectiveReportsInLast48Hours
  )

  const csmAction = derivePepCsmAction({
    pms,
    lastResult,
    pmsSyncLastInfoReceivedOn,
    pmsSyncTotalReportsReceivedInLast48Hours,
    isHavingProblems,
    pmsSyncIsConnected,
    isDisabled,
    pepDesktopAppDeviceCount,
    pepDesktopAppDistinctUserCount,
    pepDashboardUserCount,
    pepCredentialIssueKind,
    pepCredentialLastFailureReason,
    pepCredentialWorkingCount,
    pepCredentialWrongPasswordCount,
    pepCredentialChangePasswordCount,
    pepPasswordExpireDays
  })

  const csmActionClassName =
    {
      success: styles.csmActionSuccess,
      warn: styles.csmActionWarn,
      error: styles.csmActionError
    }[csmAction.severity] || styles.csmActionNeutral

  const pepDesktopAppDisplay =
    showDesktopAppVersion && pms === PMS_HILTON_PEP
      ? getPepDesktopAppVersionDisplay(
          {
            pepDesktopAppDeviceCount,
            pepDesktopAppVersions
          },
          latestDesktopRelease
        )
      : null

  const pepDesktopAppChipClassName = pepDesktopAppDisplay
    ? {
        Latest: styles.pepDesktopAppChipLatest,
        Outdated: styles.pepDesktopAppChipOutdated,
        Mixed: styles.pepDesktopAppChipOutdated,
        NoApp: styles.pepDesktopAppChipNeutral,
        Unknown: styles.pepDesktopAppChipNeutral
      }[pepDesktopAppDisplay.state] || styles.pepDesktopAppChipNeutral
    : null

  const pmsSyncLastInfoIsStale = isPmsSyncLastInfoStale(pmsSyncLastInfoReceivedOn)

  const pepBadgeClassName = pepHealthDisplay
    ? {
        AppNotInstalled: styles.pepBadgeAppNotInstalled,
        PepAuthorizationError: styles.pepBadgePepAuthorizationError,
        Working: styles.pepBadgeWorking,
        NeedsAttention: styles.pepBadgeNeedsAttention
      }[pepHealthDisplay.badgeKey] || styles.pepBadgeDefault
    : null

  const pepBadgesNeedRedSyncIcon = [
    'AppNotInstalled',
    'NeedsAttention',
    'PepAuthorizationError'
  ]

  const pepShowRedSyncProblemIcon =
    !!pepHealthDisplay &&
    pepBadgesNeedRedSyncIcon.includes(pepHealthDisplay.badgeKey)

  const hasProblemDebugPayload =
    problems != null &&
    problems !== '' &&
    !(Array.isArray(problems) && problems.length === 0) &&
    !(
      typeof problems === 'object' &&
      problems !== null &&
      !Array.isArray(problems) &&
      Object.keys(problems).length === 0
    )

  const showRedSyncAlertIcon =
    pepShowRedSyncProblemIcon ||
    isHavingProblems === true ||
    pmsSyncIsConnected === false ||
    pmsSyncLastInfoIsStale

  const showProblemDetailsRow = hasProblemDebugPayload || showRedSyncAlertIcon

  function tryLogin() {
    setIsTryLoginDialogVisible(true)
    setIsTryingLogin(true)
    const credentialString = null
    internalOnlyApi
      .tryLogin({ subsidiaryId, credentialString })
      .then(res => {
        const response = res.response

        setTryLoginResponse(response)
        setIsTryLoginSuccessful(response.isSuccess)
      })
      .catch(err => {
        setTryLoginResponse(err)
        setIsTryLoginSuccessful(false)
      })
      .finally(() => {
        setIsTryingLogin(false)
      })
  }

  function closeTryLoginDialog() {
    setIsTryLoginDialogVisible(false)
    setTryLoginResponse(null)
    setIsTryLoginSuccessful(null)
  }

  const { addToast } = useToasts()

  useEffect(() => {
    setIsPMSSyncEnabled(isDisabled ? false : true)
  }, [isDisabled])

  function setStopPMSSync(status) {
    if (canReEnablePMS) {
      setReEnablingPmsSync(true)

      addToast(status ? 'Starting PMS sync...' : 'Stopping PMS sync...', {
        appearance: 'info',
        autoDismiss: true
      })

      pmsApi
        .pmsToggle({ SubsidiaryId: subsidiaryId, Pms: pms, Status: status })
        .then(() => {
          addToast(status ? 'PMS sync started' : 'PMS sync stopped', {
            appearance: 'success',
            autoDismiss: true
          })
        })
        .catch(err => {
          addToast(err.toString(), { appearance: 'error' })
          setIsPMSSyncEnabled(status)
        })
        .finally(() => {
          setIsPMSSyncEnabled(status)
          setReEnablingPmsSync(false)
        })
    } else {
      addToast('You do not have permission to perform this action', {
        appearance: 'error'
      })
    }
  }

  function renderPepStatusContent({ simplified = false } = {}) {
    if (!pepHealthDisplay) {
      return (
        <p className={styles.lastResultPlain}>
          {lastResult
            ? lastResult.slice(0, 30)
            : isPMSSupportesLogin
            ? (
                <span style={{ position: 'relative', top: 3 }}>No credentials</span>
              )
            : (
                ''
              )}
        </p>
      )
    }

    return (
      <div className={styles.pepLastResultMain}>
        <div className={styles.pepTopLine}>
          <span className={`${styles.pepBadge} ${pepBadgeClassName}`}>
            {pepHealthDisplay.badgeLabel}
          </span>
        </div>
        {!simplified && (
          <>
            <div className={styles.pepStateTagRow}>
              <span
                className={`${styles.pepStateTag} ${
                  pepHealthDisplay.onboardingState === 'Done'
                    ? styles.pepStateTagPositive
                    : pepHealthDisplay.onboardingState === 'Pending'
                    ? styles.pepStateTagWarning
                    : styles.pepStateTagNeutral
                }`}
                title={`Onboarding status: ${
                  pepHealthDisplay.onboardingLabel || 'Unknown'
                }`}
              >
                <span className={styles.pepStateTagKey}>Onboarding</span>
                <span className={styles.pepStateTagValue}>
                  {pepHealthDisplay.onboardingLabel || 'Unknown'}
                </span>
              </span>
              <span
                className={`${styles.pepStateTag} ${
                  pepHealthDisplay.futureState === 'Active'
                    ? styles.pepStateTagPositive
                    : pepHealthDisplay.futureState === 'Inactive'
                    ? styles.pepStateTagWarning
                    : styles.pepStateTagNeutral
                }`}
                title={`Future report status: ${pepHealthDisplay.futureLabel || 'Unknown'}`}
              >
                <span className={styles.pepStateTagKey}>Future</span>
                <span className={styles.pepStateTagValue}>
                  {pepHealthDisplay.futureLabel || 'Unknown'}
                </span>
              </span>
            </div>
            {pepDesktopAppDisplay ? (
              <div
                className={`${styles.pepDesktopAppChipRow} ${pepDesktopAppChipClassName}`}
                title={pepDesktopAppDisplay.title}
              >
                <span className={styles.pepDesktopAppChipKey}>Desktop</span>
                <span className={styles.pepDesktopAppChipVersion}>
                  {pepDesktopAppDisplay.versionText}
                </span>
                <span className={styles.pepDesktopAppChipLabel}>
                  {pepDesktopAppDisplay.label}
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    )
  }

  function renderLastResultActions() {
    return (
      <div className={styles.lastResultActions}>
        {showElectronTelemetryButton && (
          <SecondarySmallButton
            className={styles.pepDetailsBtn}
            onClick={openElectronTelemetry}
          >
            View details
          </SecondarySmallButton>
        )}
        {showElectronTelemetryButton &&
          (pepHealthDisplay?.badgeKey === 'Working' ? (
            <span
              className={styles.pepPmsConnectedIcon}
              title="PMS is connected"
              role="img"
              aria-label="PMS is connected"
            >
              <CheckIcon width={16} height={16} />
            </span>
          ) : (
            <span
              className={styles.pepPmsConnectedIconSpacer}
              aria-hidden="true"
            />
          ))}
        {showProblemDetailsRow && (
          <>
            {hasProblemDebugPayload ? (
              <button type="button" onClick={() => handleSetDebugData(problems)}>
                <BugIcon width={22} fill="#cbccce" />
              </button>
            ) : (
              <span
                className={styles.pepProblemIconOnly}
                title="Problem details — no payload to open"
                role="img"
                aria-label="Problem details"
              >
                <BugIcon width={22} fill="#cbccce" />
              </span>
            )}
            {showRedSyncAlertIcon && (
              <span
                className={styles.pepProblemIconOnly}
                title="PMS sync problems"
                role="img"
                aria-label="PMS sync problems"
              >
                <BugIcon width={22} fill="red" />
              </span>
            )}
          </>
        )}
      </div>
    )
  }

  function renderPmsSyncIcon() {
    return (
      <div className={styles.pmsStatus}>
        {isDisabled ? (
          <>
            <ReactTooltip className={styles.pmsHealthyMessage} />
            <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
              <CrossIcon fill="red" />
            </span>
          </>
        ) : isAllReportHealthy && !pmsSyncLastInfoIsStale ? (
          <CheckIcon />
        ) : (
          <>
            <ReactTooltip className={styles.pmsHealthyMessage} />
            <span data-tip={pmsSyncMessage(pmsHealthyKind)}>
              <CrossIcon />
            </span>
          </>
        )}
      </div>
    )
  }
  function pmsSyncMessage(message) {
    if (message === 'PasswordWrong') {
      return 'Password is wrong'
    } else if (message === 'PasswordExpired') {
      return 'Password is expired'
    } else if (message === 'NotIntegrated') {
      return 'Never integrated'
    } else if (message === 'SyncStopped') {
      return 'Sync stopped'
    } else if (message === 'PartiallyWorking') {
      return 'Partially working'
    } else {
      return '-'
    }
  }

  const syncTimeLabel = isInProgress
    ? 'Syncing now'
    : pmsSyncLastInfoReceivedOn
    ? moment.utc(pmsSyncLastInfoReceivedOn).local().fromNow()
    : nextStartDate
    ? moment(nextStartDate).add(-1, 'day').format('MMM DD, YYYY')
    : '-'

  return (
    <div
      className={`${styles.rowHolder} ${rowStatus ? styles[rowStatus] : ''} ${
        compactPepLayout ? styles.rowHolderCompact : ''
      } ${isTelemetryHighlighted ? styles.telemetrySelected : ''}`}
      title={statusExplanation}
    >
      {isElectronTelemetryOpen && (
        <ElectronTelemetryModal
          isOpen={isElectronTelemetryOpen}
          onClose={() => setIsElectronTelemetryOpen(false)}
          subsidiaryId={subsidiaryId}
          hotelDisplayId={displayId}
          hotelLabel={`${brand || ''} ${shortName || ''}`.trim()}
          openedFromPmsHealthCheckDetails
          csmUserFullName={csmUserFullName}
          lastResult={lastResult}
          pmsSyncLastInfoReceivedOn={pmsSyncLastInfoReceivedOn}
          pmsSyncTotalReportsReceivedInLast48Hours={pmsSyncTotalReportsReceivedInLast48Hours}
          isHavingProblems={isHavingProblems}
          pmsSyncIsConnected={pmsSyncIsConnected}
          isDisabled={isDisabled}
          pepDesktopAppDeviceCount={pepDesktopAppDeviceCount}
          pepDesktopAppDistinctUserCount={pepDesktopAppDistinctUserCount}
          pepDashboardUserCount={pepDashboardUserCount}
          pepCredentialIssueKind={pepCredentialIssueKind}
          pepCredentialLastFailureReason={pepCredentialLastFailureReason}
          pepCredentialWorkingCount={pepCredentialWorkingCount}
          pepCredentialWrongPasswordCount={pepCredentialWrongPasswordCount}
          pepCredentialChangePasswordCount={pepCredentialChangePasswordCount}
          pepPasswordExpireDays={pepPasswordExpireDays}
        />
      )}
      {isTryLoginDialogVisible && (
        <TryLoginDialog
          isTryLoginSuccessful={isTryLoginSuccessful}
          isTryingLogin={isTryingLogin}
          onClose={closeTryLoginDialog}
          response={tryLoginResponse}
          brand={brand}
          shortName={shortName}
        />
      )}
      <div className={styles.index}>{index}</div>
      <div className={styles.statusIcon}>
        <HotelStatusIcon status={subsidiaryStatus} />
      </div>
      <Row
        className={[
          styles.syncTableRow,
          compactPepLayout ? layoutStyles.pepCompactGridRowBody : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <HotelIdCell displayId={displayId} />
        <HotelNameCell
          brand={brand}
          name={shortName}
          groupName={groupName}
          navigationControl={
            <Link to={`/s/${subsidiaryId}/hotelDetails`}>
              <button>Go to hotel details →</button>
            </Link>
          }
        />
        {!compactPepLayout && <Spacer />}
        <Cell className={styles.textCell}>
          <span title={csmUserFullName}>
            {csmUserFullName
              ?.split(' ')
              .map(str => str[0])
              .join('.')}
          </span>
        </Cell>
        {compactPepLayout ? (
          <>
            <Cell className={styles.compactSyncCell}>
              {renderPmsSyncIcon()}
              <div className={styles.compactSyncMeta}>
                <span title={pmsSyncLastInfoReceivedOn || ''}>{syncTimeLabel}</span>
                <span>{effectiveReportsInLast48Hours} reports / 48h</span>
              </div>
            </Cell>
            <Cell
              className={`${styles.csmActionCell} ${csmActionClassName}`}
              title={csmAction.actionInstruction}
            >
              <span className={styles.csmActionLabel}>{csmAction.actionLabel}</span>
            </Cell>
            <Cell
              className={`${styles.pepStatusCellCompact} ${
                pepHealthDisplay ? styles.lastResultCellCompact : ''
              }`}
            >
              {renderPepStatusContent({ simplified: true })}
            </Cell>
            <Cell className={styles.pepDetailsCellCompact}>{renderLastResultActions()}</Cell>
          </>
        ) : (
          <>
            <Cell>{renderPmsSyncIcon()}</Cell>
            <Cell className={styles.textCell}>
              {isInProgress
                ? 'Syncing now'
                : pmsSyncLastInfoReceivedOn
                ? moment.utc(pmsSyncLastInfoReceivedOn).local().format('MMM DD, YYYY h:mm A')
                : nextStartDate
                ? moment(nextStartDate).add(-1, 'day').format('MMM DD, YYYY')
                : '-'}
            </Cell>
            <NumberCell>{pmsSyncTotalReportsReceivedInLast48Hours}</NumberCell>
            <Cell className={styles.textCell}>{PMSnames[pms]}</Cell>
            <Cell
              className={`${styles.doubleCell} ${
                pepHealthDisplay ? styles.lastResultCellCompact : ''
              }`}
            >
              <div className={styles.lastResultInner}>
                {renderPepStatusContent()}
                {renderLastResultActions()}
              </div>
            </Cell>
          </>
        )}

        {hideStopTestLogin
          ? []
          : [
              <Cell key="hc-pms-stop-test" className={styles.bugButtonCell}>
                {pms === PMS_HILTON_PEP ? (
                  <span className={styles.pepStopColumnDash} title="Not applicable for Hilton PEP">
                    —
                  </span>
                ) : (
                  <>
                    {canReEnablePMS && (
                      <Toggle
                        className={styles.groupToggle}
                        label=""
                        disabled={reEnablingPmsSync}
                        checked={isPMSSyncEnabled}
                        handleChange={e => setStopPMSSync(e.target.checked)}
                      />
                    )}
                    {PMSsWithSyncLoginSupport.includes(pms) && (
                      <SecondarySmallButton
                        style={{ transform: 'scale(0.9)' }}
                        onClick={tryLogin}
                      >
                        Test login
                      </SecondarySmallButton>
                    )}
                  </>
                )}
              </Cell>
            ]}
        <NoteCell
          note={note}
          setNote={setNote}
          saveImmediately={saveImmediately}
          loadNoteOnDemand={loadNoteOnDemand}
          isLoading={isLoadingNote}
          isSaving={isSaving}
          error={noteError}
        />
      </Row>
    </div>
  )
}

export default SyncRow
